import React, { useState, useRef, useCallback, useEffect } from "react";
import { TextField, InputAdornment, IconButton, GlobalStyles } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { loadGoogleMaps } from "../../utils/googleMapsLoader";
import { mergeTextFieldSlotProps } from "../../utils/muiTextFieldSlots";

const DEFAULT_PLACES_TYPES = ["address"];

/** Stable reference for legacy `types: ["geocode"]` autocomplete behavior. */
export const PLACES_TYPES_GEOCODE = ["geocode"];

/**
 * Parses Google Places address_components into address parts.
 * When separateCounty is true, administrative_area_level_2 is county only (not used as city fallback).
 */
function parseAddressComponents(addressComponents, separateCounty) {
  let address = "";
  let city = "";
  let state = "";
  let county = "";
  let zipCode = "";

  addressComponents.forEach((component) => {
    const types = component.types;

    if (types.includes("street_number")) {
      address = `${component.long_name} ${address}`;
    }
    if (types.includes("route")) {
      address += component.long_name;
    }

    if (types.includes("locality")) {
      city = component.long_name;
    }
    if (!city && (types.includes("sublocality") || types.includes("sublocality_level_1"))) {
      city = component.long_name;
    }
    if (types.includes("administrative_area_level_2")) {
      if (separateCounty) {
        county = component.long_name;
      } else if (!city) {
        city = component.long_name;
      }
    }

    if (types.includes("administrative_area_level_1")) {
      state = component.long_name;
    }

    if (types.includes("postal_code")) {
      zipCode = component.long_name;
    }
  });

  return { address, city, state, county, zipCode };
}

function applyGeocoderFallback(location, separateCounty, parts, dispatch) {
  const geocoder = new window.google.maps.Geocoder();
  geocoder.geocode({ location }, (results, status) => {
    if (status !== "OK" || !results[0]) return;
    let city = parts.city;
    let state = parts.state;
    let county = parts.county;
    results[0].address_components.forEach((component) => {
      const types = component.types;
      if (!city && types.includes("locality")) {
        city = component.long_name;
      }
      if (!state && types.includes("administrative_area_level_1")) {
        state = component.long_name;
      }
      if (separateCounty && !county && types.includes("administrative_area_level_2")) {
        county = component.long_name;
      }
    });
    dispatch({ ...parts, city, state, county });
  });
}

const REGISTER_PAC_GLOBAL_STYLES = (
  <GlobalStyles
    styles={{
      ".pac-container": {
        zIndex: "99999 !important",
        background: "white !important",
        border: "1px solid #ccc !important",
        borderRadius: "4px !important",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1) !important",
        fontFamily: "Arial, sans-serif !important",
      },
      ".pac-item": {
        padding: "8px 12px !important",
        cursor: "pointer !important",
        borderBottom: "1px solid #eee !important",
        fontSize: "14px !important",
        color: "#333 !important",
      },
      ".pac-item:last-child": { borderBottom: "none !important" },
      ".pac-item:hover": { backgroundColor: "#f5f5f5 !important" },
      ".pac-item-query": { fontWeight: 600, color: "#333 !important" },
      ".pac-matched": { fontWeight: "bold !important", color: "#1a73e8 !important" },
      ".pac-icon": { marginRight: "8px !important" },
      ".MuiDialog-root .pac-container": { zIndex: "99999 !important" },
    }}
  />
);

/**
 * Google Places Autocomplete wrapped in MUI TextField.
 * @param {null|false|'start'|'end'} [searchIconPosition] — `'start'`/`'end'` shows search adornment; omit or `null` for none
 */
export function GoogleAutoCompleteField({
  placeholder,
  callback,
  sx,
  disabled = false,
  label,
  fullWidth = true,
  includeCounty = false,
  callbackObject = false,
  clearOnSelect = false,
  searchIconPosition = null,
  searchIconButtonDisabled = false,
  disableUntilLoaded = false,
  initDelayMs = 0,
  placesTypes = DEFAULT_PLACES_TYPES,
  pacZIndex,
  registerPacStyles = false,
  slotProps: slotPropsProp,
  ...textFieldRest
}) {
  const autoCompleteRef = useRef(null);
  const inputRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const separateCounty = includeCounty || callbackObject;

  const loadGoogleMapsScript = useCallback(() => {
    loadGoogleMaps()
      .then(() => setLoaded(true))
      .catch((e) => console.error(e));
  }, []);

  useEffect(() => {
    loadGoogleMapsScript();
  }, [loadGoogleMapsScript]);

  useEffect(() => {
    if (!loaded || !window.google?.maps?.places) return undefined;

    let cancelled = false;
    let listener = null;
    let timeoutId;

    const attach = () => {
      if (cancelled || !inputRef.current) return;
      try {
        const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
          fields: ["address_components", "geometry"],
          types: placesTypes,
        });
        autoCompleteRef.current = ac;

        const dispatchPlaceComponents = (p) => {
          if (clearOnSelect && inputRef.current) {
            inputRef.current.value = "";
          }
          if (!callback) return;
          if (callbackObject) {
            callback({
              address: p.address,
              city: p.city,
              state: p.state,
              county: p.county,
              zipCode: p.zipCode,
            });
          } else if (includeCounty) {
            callback(p.address, p.city, p.state, p.county, p.zipCode);
          } else {
            callback(p.address, p.city, p.state, p.zipCode);
          }
        };

        listener = ac.addListener("place_changed", () => {
          const place = ac.getPlace();
          if (!place?.address_components) {
            console.error("No valid place returned or missing address components.");
            return;
          }

          let parts = parseAddressComponents(place.address_components, separateCounty);

          if ((!parts.city || !parts.state) && place.geometry?.location) {
            applyGeocoderFallback(place.geometry.location, separateCounty, parts, dispatchPlaceComponents);
          } else {
            dispatchPlaceComponents(parts);
          }
        });
      } catch (error) {
        console.error("Error initializing Google Autocomplete:", error);
      }
    };

    if (initDelayMs > 0) {
      timeoutId = window.setTimeout(attach, initDelayMs);
    } else {
      attach();
    }

    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
      if (listener != null && window.google?.maps?.event) {
        window.google.maps.event.removeListener(listener);
      }
    };
  }, [
    loaded,
    callback,
    includeCounty,
    callbackObject,
    clearOnSelect,
    placesTypes,
    initDelayMs,
    separateCounty,
  ]);

  const mergedDisabled = disabled || (disableUntilLoaded && !loaded);

  let adornment = null;
  if (searchIconPosition === "start") {
    adornment = (
      <InputAdornment position="start">
        <SearchIcon sx={{ color: "text.secondary" }} />
      </InputAdornment>
    );
  } else if (searchIconPosition === "end") {
    adornment = (
      <InputAdornment position="end">
        <IconButton size="small" aria-label="search" disabled={searchIconButtonDisabled || mergedDisabled}>
          <SearchIcon />
        </IconButton>
      </InputAdornment>
    );
  }

  const inputSlotPatch =
    adornment != null
      ? {
          ...(searchIconPosition === "start" ? { startAdornment: adornment } : {}),
          ...(searchIconPosition === "end" ? { endAdornment: adornment } : {}),
        }
      : undefined;

  const mergedSlotProps = mergeTextFieldSlotProps(slotPropsProp, {
    ...(inputSlotPatch ? { input: inputSlotPatch } : {}),
  });

  const pacInjection =
    typeof pacZIndex === "number" ? (
      <GlobalStyles styles={{ ".pac-container": { zIndex: `${pacZIndex} !important` } }} />
    ) : null;

  return (
    <>
      {registerPacStyles ? REGISTER_PAC_GLOBAL_STYLES : pacInjection}
      <TextField
        label={label}
        variant="outlined"
        fullWidth={fullWidth}
        placeholder={placeholder}
        inputRef={inputRef}
        disabled={mergedDisabled}
        sx={sx}
        slotProps={mergedSlotProps}
        {...textFieldRest}
      />
    </>
  );
}

export { GoogleAutoCompleteField as GoogleAutoComplete };
export default GoogleAutoCompleteField;
